import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { filterTitles, paginate, sortTitles, SORT_OPTIONS } from "@/lib/catalog";
import { topics } from "@/mocks/topics";
import type { SortKey } from "@/types/film";
import TitleGrid from "@/components/feature/TitleGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterPanel from "@/pages/category/components/FilterPanel";

function parseList(value: string | null): string[] {
  if (!value) return [];
  return value.split(",").filter(Boolean);
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const typeParam = searchParams.get("type");
  const type: "all" | "movie" | "tv" =
    typeParam === "movie" || typeParam === "tv" ? typeParam : "all";
  const genres = parseList(searchParams.get("genres"));
  const regions = parseList(searchParams.get("regions"));
  const years = parseList(searchParams.get("years"));
  const topicId = searchParams.get("topic") ?? "";
  const keyword = searchParams.get("q") ?? "";
  const sort = (searchParams.get("sort") as SortKey) || "popularity";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const topic = topics.find((t) => t.id === topicId);

  const heading = topic
    ? topic.name
    : type === "movie"
    ? "欧美电影"
    : type === "tv"
    ? "欧美电视剧"
    : "全部影视";
  const description = topic
    ? topic.subtitle
    : type === "movie"
    ? "浏览我们精选的欧美电影资源，涵盖最新与经典作品。"
    : type === "tv"
    ? "浏览我们精选的欧美电视剧资源，涵盖热播与经典剧集。"
    : "浏览本站收录的欧美电影与电视剧资源，支持多条件筛选。";

  const pageTitle = `${heading} - 欧美精品在线观看`;

  usePageMeta(pageTitle, description, "/category");

  const filtered = useMemo(
    () =>
      sortTitles(
        filterTitles({
          type,
          genres,
          regions,
          yearRanges: years,
          topicTag: topic?.tag ?? "",
          keyword,
        }),
        sort
      ),
    [type, genres, regions, years, topic, keyword, sort]
  );

  const { items, totalPages, page: safePage, total } = paginate(filtered, page);

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next, { replace: true });
  };

  const toggleMulti = (key: "genres" | "regions" | "years", value: string) => {
    const current = parseList(searchParams.get(key));
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    update({ [key]: next.join(","), page: "1" });
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (type !== "all") next.set("type", type);
    if (topicId) next.set("topic", topicId);
    setSearchParams(next, { replace: true });
    setMobileFilterOpen(false);
  };

  const hasActiveFilter = genres.length + regions.length + years.length > 0;
  const typeTabs: { key: "all" | "movie" | "tv"; label: string }[] = [
    { key: "all", label: "全部" },
    { key: "movie", label: "电影" },
    { key: "tv", label: "电视剧" },
  ];

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
      <nav aria-label="面包屑" className="flex items-center gap-1 text-xs text-foreground-400">
        <Link to="/" className="transition-colors hover:text-accent-400 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        {topic ? (
          <>
            <Link to="/category" className="transition-colors hover:text-accent-400 cursor-pointer">
              专题
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-200">{topic.name}</span>
          </>
        ) : (
          <span className="text-foreground-200">{heading}</span>
        )}
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">{heading}</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-400">{description}</p>
      </header>

      <div className="mt-6 flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
        {typeTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => update({ type: tab.key === "all" ? null : tab.key, topic: null, page: "1" })}
            className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors cursor-pointer ${
              type === tab.key && !topic
                ? "bg-primary-500 font-medium text-foreground-50"
                : "border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-foreground-50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-lg border border-background-800 bg-background-900 p-5">
            <FilterPanel
              genres={genres}
              regions={regions}
              years={years}
              onToggleGenre={(v) => toggleMulti("genres", v)}
              onToggleRegion={(v) => toggleMulti("regions", v)}
              onToggleYear={(v) => toggleMulti("years", v)}
              onReset={resetFilters}
            />
          </div>
        </aside>

        <div className="min-w-0">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-800 bg-background-900 px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileFilterOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-background-700 px-3 py-1.5 text-sm text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 lg:hidden whitespace-nowrap cursor-pointer"
              >
                <i className="ri-filter-3-line" />
                筛选
                {hasActiveFilter ? (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-500 text-[10px] text-foreground-950">
                    {genres.length + regions.length + years.length}
                  </span>
                ) : null}
              </button>
              <span className="text-sm text-foreground-400">
                共 <span className="font-medium text-foreground-100">{total}</span> 部作品
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-foreground-500 sm:block">排序</span>
              <select
                value={sort}
                onChange={(e) => update({ sort: e.target.value, page: "1" })}
                aria-label="排序方式"
                className="rounded-md border border-background-700 bg-background-950 px-3 py-1.5 text-sm text-foreground-100 outline-none transition-colors focus:border-primary-500 cursor-pointer"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id} className="bg-background-900">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {items.length > 0 ? (
            <>
              <TitleGrid items={items} className="lg:grid-cols-3 xl:grid-cols-4" />
              <Pagination
                page={safePage}
                totalPages={totalPages}
                onChange={(p) => update({ page: String(p) })}
              />
            </>
          ) : (
            <EmptyState
              title="未找到符合条件的影片"
              description="当前筛选组合没有匹配的结果，试试减少筛选条件，或重置后重新浏览。"
              actionLabel="重置筛选条件"
              onAction={resetFilters}
            />
          )}
        </div>
      </div>

      {mobileFilterOpen ? (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-background-950/70"
            onClick={() => setMobileFilterOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-lg border-t border-background-800 bg-background-900 p-5">
            <div className="mb-4 flex items-center justify-between">
              <span className="font-heading text-base font-bold text-foreground-50">筛选条件</span>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-700 text-foreground-200 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterPanel
              genres={genres}
              regions={regions}
              years={years}
              onToggleGenre={(v) => toggleMulti("genres", v)}
              onToggleRegion={(v) => toggleMulti("regions", v)}
              onToggleYear={(v) => toggleMulti("years", v)}
              onReset={resetFilters}
            />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 py-2.5 text-sm font-medium text-foreground-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              查看筛选结果
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}